import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { Button, Spin, Table, TableColumnsType, Tag, Typography } from "antd";
import { useState } from "react";

import ModalViewReturnSlip from "../../components/modal/Warehouse/ModalViewReturnSlip";
import { IFilterWarehouse, IReturnSlipWarehouse } from "../../models/Warehouse";
import { useGetReturnSlipWarehouse } from "../../query/WarehouseQuery";

const { Text } = Typography;

const WarehouseReturn = () => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    returnSlipId: number | null;
  }>({
    open: false,
    returnSlipId: null,
  });

  const [query, setQuery] = useState<IFilterWarehouse>({
    page: 1,
    limit: 10,
  });

  const { data, isLoading } = useGetReturnSlipWarehouse(query);

  const getStatusConfig = (
    status: "PENDING" | "APPROVED" | "REJECTED",
  ): { color: string; icon: React.ReactNode; text: string } => {
    switch (status) {
      case "APPROVED":
        return {
          color: "success",
          icon: <CheckCircleOutlined />,
          text: "Đã duyệt",
        };
      case "REJECTED":
        return {
          color: "error",
          icon: <CloseCircleOutlined />,
          text: "Đã từ chối",
        };
      case "PENDING":
      default:
        return {
          color: "warning",
          icon: <ClockCircleOutlined />,
          text: "Chờ duyệt",
        };
    }
  };

  const columns: TableColumnsType<IReturnSlipWarehouse> = [
    {
      title: "STT",
      key: "index",
      width: 60,
      align: "center",
      render: (_: unknown, __: IReturnSlipWarehouse, index: number) =>
        (query.page! - 1) * query.limit! + index + 1,
    },
    {
      title: "Mã phiếu",
      dataIndex: "id",
      key: "id",
      width: 100,
      align: "center",
      render: (id: number) => (
        <Tag color="blue" icon={<TagOutlined />}>
          #{id}
        </Tag>
      ),
    },
    {
      title: "Tên phiếu hoàn trả",
      dataIndex: "slipName",
      key: "slipName",
      width: 250,
      render: (text: string) => (
        <Text strong className="text-blue-600">
          <FileTextOutlined className="mr-2" />
          {text}
        </Text>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 150,
      align: "center",
      filters: [
        { text: "Chờ duyệt", value: "PENDING" },
        { text: "Đã duyệt", value: "APPROVED" },
        { text: "Đã từ chối", value: "REJECTED" },
      ],
      onFilter: (value: any, record: IReturnSlipWarehouse) =>
        record.status === value,
      render: (status: "PENDING" | "APPROVED" | "REJECTED") => {
        const config = getStatusConfig(status);
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "Thời gian tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      align: "center",
      sorter: (a: IReturnSlipWarehouse, b: IReturnSlipWarehouse) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date: Date) => (
        <Text>{new Date(date).toLocaleString("vi-VN")}</Text>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_: unknown, record: IReturnSlipWarehouse) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setModalState({ open: true, returnSlipId: record.id })}
        >
          Chi tiết
        </Button>
      ),
    },
  ];

  return (
    <div>
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={data?.data}
            rowKey="id"
            pagination={{
              current: data?.pagination.page,
              pageSize: data?.pagination.limit,
              total: data?.pagination?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Tổng ${total} phiếu`,
              onChange: (newPage, newPageSize) => {
                setQuery({ ...query, page: newPage, limit: newPageSize });
              },
            }}
            scroll={{ x: 1000 }}
          />
        </>
      )}

      <ModalViewReturnSlip
        open={modalState.open}
        onClose={() => setModalState({ open: false, returnSlipId: null })}
        returnSlipId={modalState.returnSlipId}
      />
    </div>
  );
};

export default WarehouseReturn;
